import { apiFetch } from "../api.js";
import type {
  CreateSpendingTransactionInput,
  SpendingTransaction,
  SpendingTransactionsListResponse,
  UpdateSpendingTransactionInput,
} from "./types.js";

/**
 * Spending transactions API module — Phase 5 Session 2.
 *
 * - List requires `from` + `to` (ISO dates). Optional `category_id` +
 *   `account_id` filters narrow the result set.
 * - GET / PATCH / DELETE use a two-segment path (`{date}/{tx_id}`)
 *   because the table SK is `<date>#<tx_id>`.
 * - DELETE is **hard** with audit row (consumer transactions get
 *   corrected, not retained for compliance).
 */

export interface ListSpendingTransactionsParams {
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
  categoryId?: string;
  accountId?: string;
}

export function listSpendingTransactions(
  params: ListSpendingTransactionsParams,
): Promise<SpendingTransactionsListResponse> {
  const qs = new URLSearchParams();
  qs.set("from", params.from);
  qs.set("to", params.to);
  if (params.categoryId) qs.set("category_id", params.categoryId);
  if (params.accountId) qs.set("account_id", params.accountId);
  return apiFetch<SpendingTransactionsListResponse>(
    `/v1/spending-transactions?${qs.toString()}`,
  );
}

export function createSpendingTransaction(
  input: CreateSpendingTransactionInput,
): Promise<SpendingTransaction> {
  return apiFetch<SpendingTransaction>("/v1/spending-transactions", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function getSpendingTransaction(
  date: string,
  txId: string,
): Promise<SpendingTransaction> {
  return apiFetch<SpendingTransaction>(
    `/v1/spending-transactions/${date}/${txId}`,
  );
}

export function updateSpendingTransaction(
  date: string,
  txId: string,
  input: UpdateSpendingTransactionInput,
): Promise<SpendingTransaction> {
  return apiFetch<SpendingTransaction>(
    `/v1/spending-transactions/${date}/${txId}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export function deleteSpendingTransaction(
  date: string,
  txId: string,
): Promise<void> {
  return apiFetch<void>(`/v1/spending-transactions/${date}/${txId}`, {
    method: "DELETE",
  });
}
